import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { TrendingDown, Zap, BarChart, Lock } from 'lucide-react';
import './BusinessImpact.css';

const BusinessImpact = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const [counters, setCounters] = useState({
    cost: 0,
    speed: 0,
    accuracy: 0,
    control: 0,
  });

  useEffect(() => {
    if (inView) {
      const duration = 2000; // 2 seconds
      const steps = 60;
      const stepDuration = duration / steps;

      let currentStep = 0;
      const interval = setInterval(() => {
        currentStep++;
        const progress = currentStep / steps;

        setCounters({
          cost: Math.floor(60 * progress),
          speed: Math.floor(85 * progress),
          accuracy: Math.floor(95 * progress),
          control: Math.floor(90 * progress),
        });

        if (currentStep >= steps) {
          clearInterval(interval);
        }
      }, stepDuration);

      return () => clearInterval(interval);
    }
  }, [inView]);

  const impacts = [
    {
      icon: <TrendingDown size={40} />,
      value: counters.cost,
      suffix: '%',
      label: 'Reduction in manpower costs',
      color: '#00D4FF',
    },
    {
      icon: <Zap size={40} />,
      value: counters.speed,
      suffix: '%',
      label: 'Faster operations',
      color: '#FFEAA7',
    },
    {
      icon: <BarChart size={40} />,
      value: counters.accuracy,
      suffix: '%',
      label: 'Higher accuracy & efficiency',
      color: '#74B9FF',
    },
    {
      icon: <Lock size={40} />,
      value: counters.control,
      suffix: '%',
      label: 'Improved operational control',
      color: '#A29BFE',
    },
  ];

  return (
    <section className="impact-section" ref={ref} data-testid="impact-section">
      <div className="container">
        <motion.div
          className="impact-header"
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
        >
          <h2 className="section-title" data-testid="impact-title">
            Business Impact
          </h2>
          <p className="section-subtitle" data-testid="impact-subtitle">
            Clients Using DanstonAI Experience:
          </p>
        </motion.div>

        <div className="impact-grid">
          {impacts.map((impact, index) => (
            <motion.div
              key={index}
              className="impact-card"
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: index * 0.15, duration: 0.5, ease: 'easeOut' }}
              data-testid={`impact-${index}`}
            >
              <div className="impact-icon" style={{ color: impact.color }}>
                {impact.icon}
              </div>
              <div className="impact-value" style={{ color: impact.color }}>
                {impact.value}{impact.suffix}
              </div>
              <p className="impact-label">{impact.label}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default BusinessImpact;
